import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import ToggleTop from "../common/ToggleTop";
import { useUser } from "../../providers/UserProvider";
import RestrictedIcon from "../icons/RestrictedIcon";
import { StackHeader, TabHeader } from "./app/AppHeader";
import SideMenu from "./app/SideMenu";
import TabBar, { tabForPath } from "./app/TabBar";
import ActionMenu from "./app/ActionMenu";
import AddSheet from "./app/AddSheet";
import { isAdmin } from "../../pages/Admin/paths";

// Pages opened from a device (its details and the screens behind them). The
// app hides the tab bar on these until you go back.
const DEVICE_PAGE = /^\/(device|device-automations|editdevice|runtime|energy-stats|energy-graph|energy-expense|remotes?|device-settings\/.*|switchdetail|sensordetail|controllerdetail|securitydetail|energymeterdetail)$/;

// Titles for pages that don't pass their own.
const PAGE_TITLES = {
  "/ruleengine": "Rule Engine",
  "/scenes": "Scenes",
  "/profile": "Profile",
  "/routines": "Routines",
  "/recharge": "Recharge",
};

// Page shell in the mobile app's layout: a header (the "+" on Home, the tab
// title on the other tabs, or a back arrow on pages opened from another page),
// the side menu, and the bottom tab bar with the "+" action menu.
//
// header: "home" | "tab" | "stack" | "none". Pages that pass nothing get a
// back-arrow header titled after the page or the device it shows. Content sits
// in the page column unless bleed is set. Device screens hide the tab bar, as
// the app does. className is added to the page shell.
function Main({ children, header, title, headerRight, onBack, bleed = false, hideTabBar = false, className = "" }) {
  const { pathname, state } = useLocation();
  const { subscription, toggleTop, selectedPropertey, getProperty } = useUser();
  const [menuOpen, setMenuOpen] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  useEffect(() => {
    getProperty();
    selectedPropertey();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeMenu = useCallback(() => setMenuOpen(false), []);
  const closeActions = useCallback(() => setActionsOpen(false), []);

  const kind = header ?? "stack";
  const pageTitle = title ?? PAGE_TITLES[pathname.toLowerCase()] ?? state?.data?.name ?? "";
  // The admin account maintains the system and has no dashboard plan of its own.
  const locked = subscription === false && !isAdmin();
  const noTabBar = hideTabBar || DEVICE_PAGE.test(pathname.toLowerCase());

  return (
    <div className={`hx-app ${locked ? "is-locked" : ""} ${menuOpen ? "is-menu-open" : ""} ${noTabBar ? "no-tabbar" : ""} ${className}`}>
      {locked && (
        <div className="hx-locked" role="alert">
          <RestrictedIcon />
          <p>
            Contact <b>HASHh</b> Team to access
          </p>
          <a href="https://www.hashh.io/contact-us/">support@hashh</a>
        </div>
      )}
      {toggleTop.toggle && <ToggleTop />}

      {kind === "home" || kind === "tab" ? (
        <TabHeader
          title={kind === "tab" ? pageTitle : null}
          menuOpen={menuOpen}
          onMenu={() => setMenuOpen(!menuOpen)}
          onAdd={() => setAddOpen(true)}
        />
      ) : kind === "stack" ? (
        <StackHeader title={pageTitle} onBack={onBack} right={headerRight} />
      ) : null}

      <SideMenu open={menuOpen} onClose={closeMenu} />

      <main>{bleed ? children : <div className="hx-page">{children}</div>}</main>

      {noTabBar ? null : (
        <>
          <ActionMenu open={actionsOpen} onClose={closeActions} />
          <TabBar active={tabForPath(pathname)} actionsOpen={actionsOpen} onActions={setActionsOpen} />
        </>
      )}
      <AddSheet open={addOpen} onClose={() => setAddOpen(false)} />
    </div>
  );
}

export default Main;
